import { memo, useEffect, useMemo, useState } from "react";
import {
  AVAILABILITY_LOG_TYPE,
  BUTTON_VARIANTS,
  COLORS,
  DATE_TIME_FORMATS,
} from "@miplatform/constants";
import {
  formatDate,
  getDate,
  isFullDayAvailabilityLog,
} from "@miplatform/utils";
import { mdiClipboardAccountOutline } from "@mdi/js";
import { Box } from "@mui/material";
import { useTheme } from "@mui/system";
import { MiCheckbox } from "../MiCheckbox/MiCheckbox";
import { MiModal } from "../MiModal/MiModal";
import { useRemoveInactiveModalViewModel } from "./useRemoveInactiveModalViewModel";

/**
 * Modal component for removing inactive periods from a user's availability log.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Object} props.user - User object with availabilityLog array
 * @param {string} props.user.name - User's name
 * @param {Array} [props.user.availabilityLog] - Array of availability log entries
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onRefresh - Callback to refresh data after removal
 * @returns {JSX.Element} Modal component for managing inactive status removal
 */
export const RemoveInactiveModal = memo(
  ({ open, user, onClose, onRefresh }) => {
    const theme = useTheme();
    const [selectedIds, setSelectedIds] = useState([]);

    const { isLoading, deleteUserLogs } = useRemoveInactiveModalViewModel({
      user,
      onClose,
      onRefresh,
    });

    const logsKey = JSON.stringify(
      (user?.availabilityLog || [])
        .filter((log) => log.type === AVAILABILITY_LOG_TYPE.INACTIVE)
        .map((log) => log.id)
        .sort((a, b) => a - b)
    );

    const inactiveLogs = useMemo(() => {
      return (
        user?.availabilityLog
          ?.filter((log) => log.type === AVAILABILITY_LOG_TYPE.INACTIVE)
          .sort((a, b) => new Date(a.from) - new Date(b.from)) || []
      );
    }, [logsKey]);

    useEffect(() => {
      setSelectedIds([]);
    }, [open]);

    return (
      <MiModal
        title={`Remove ${user?.name ?? user?.displayName}'s Inactive Status`}
        titleIcon={mdiClipboardAccountOutline}
        leftIconColor={theme.palette.primary.main}
        open={open}
        description={`Select the inactive periods to remove for ${
          user?.name ?? user?.displayName
        }.`}
        closeModal={onClose}
        loading={isLoading}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            color: COLORS.BLACK,
            onClick: onClose,
          },
          {
            name: "Remove Inactive",
            variant: BUTTON_VARIANTS.CONTAINED,
            disabled: selectedIds.length === 0 || isLoading,
            onClick: () => deleteUserLogs(selectedIds),
          },
        ]}
      >
        <Box display="flex" flexDirection="column" gap={1.5}>
          {inactiveLogs.length === 0 ? (
            <Box>No inactive periods found.</Box>
          ) : (
            inactiveLogs.map((log) => {
              const fullDay = isFullDayAvailabilityLog(log);
              let label;
              if (fullDay) {
                const logDate = getDate(log.from);
                label = formatDate(
                  logDate,
                  DATE_TIME_FORMATS.FORMAT_MMMM_Do_YYYY
                );
              } else {
                label = `${formatDate(
                  log.from,
                  DATE_TIME_FORMATS.HOURS_MINUTES
                )} - ${formatDate(log.to, DATE_TIME_FORMATS.HOURS_MINUTES)}`;
              }

              return (
                <MiCheckbox
                  key={log.id}
                  label={label}
                  value={selectedIds.some((item) => item.id === log.id)}
                  handleChange={({ target: { checked } }) => {
                    if (checked) {
                      setSelectedIds((prev) => [
                        ...prev,
                        { id: log.id, modifiedOn: log.modifiedOn },
                      ]);
                    } else {
                      setSelectedIds((prev) =>
                        prev.filter((item) => item.id !== log.id)
                      );
                    }
                  }}
                />
              );
            })
          )}
        </Box>
      </MiModal>
    );
  }
);
